import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import type { StateInfo } from "../types/UserTypes";
import type { TenantProfileCreateUpdateModel } from "../types/apiSchemaAliases";
import styles from './Company.module.css';
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";

const TENANT_PROFILE_BASE = `tenantprofile`;
const FALLBACK_TIME_ZONE = "America/Chicago";
type TenantProfileFormModel = TenantProfileCreateUpdateModel & { timeZoneId?: string };

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
};

const EMPTY_TENANT_PROFILE = {
  businessName: "",
  firstName: "",
  lastName: "",
  streetAddress: "",
  city: "",
  state: 48,
  zipCode: "",
  phoneNumber: "",
  taxId: "",
  emailAddress: "",
  timeZoneId: getBrowserTimeZone(),
};

const truncateFileName = (fileName: any, maxLength = 32) => {
  if (!fileName) return "";
  if (fileName.length <= maxLength) return fileName;

  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) {
    return `${fileName.slice(0, maxLength - 3)}...`;
  }

  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  const allowedBaseLength = Math.max(8, maxLength - extension.length - 3);

  return `${baseName.slice(0, allowedBaseLength)}...${extension}`;
};

const Company = () => {
  const authRequest = { ...loginRequest };
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const { hasFeature } = useTier();
  const showLogo = hasFeature("client_ready_reports");

  const fileInputRef = useRef<any>(null);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;

    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const [tenantProfile, setTenantProfile] = useState<TenantProfileFormModel>(EMPTY_TENANT_PROFILE);
  const [states, setStates] = useState<StateInfo[]>([]);

  const [companyLogoUrl, setCompanyLogoUrl] = useState("");
  const [companyLogoFileName, setCompanyLogoFileName] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);

  const [updateStatus, setUpdateStatus] = useState<"success" | "error" | null>(null);
  const [updateMessage, setUpdateMessage] = useState("");
  const [taxIdVisible, setTaxIdVisible] = useState(false);
  const [revealedTaxId, setRevealedTaxId] = useState<string | null>(null);
  const [timeZoneLoadedFromApi, setTimeZoneLoadedFromApi] = useState(false);
  const [timeZoneTouched, setTimeZoneTouched] = useState(false);

  const toggleTaxIdVisibility = async () => {
    if (taxIdVisible) {
      if (!api) return;
      let freshTaxId = "";
      try {
        const data = await api.get(TENANT_PROFILE_BASE);
        const p = Array.isArray(data) ? data[0] : data;
        freshTaxId = p?.taxId ?? "";
      } catch { /* keep current state */ }

      if (!freshTaxId) return; // no saved value — stay in edit mode

      setTenantProfile((prev: any) => prev ? { ...prev, taxId: freshTaxId } : prev);
      setTaxIdVisible(false);
      setRevealedTaxId(null);
      return;
    }
    if (!api) return;
    try {
      const data = await api.get("tenantprofile/taxid");
      const real = typeof data === "string" ? data : (data as any)?.taxId ?? "";
      setRevealedTaxId(real);
      setTaxIdVisible(true);
    } catch {
      setRevealedTaxId(null);
    }
  };

  const showStatus = (status: any, message: any) => {
    setUpdateStatus(status);
    setUpdateMessage(message);
  };

  const clearLogoState = () => {
    setCompanyLogoUrl("");
    setCompanyLogoFileName("");
  };

  const syncLogoState = (data: any) => {
    setCompanyLogoUrl(data?.url || "");
    setCompanyLogoFileName(data?.fileName || "");
  };

  const loadStates= useCallback(async () => {
    if (!api) return [];

    const data = await api.get("stateinfo/");
    const list = Array.isArray(data) ? data : [];
    setStates(list);
    return list;
  }, [api]);

  const loadTenantProfile = useCallback(async () => {
    if (!api) return;

    const data = await api.get(TENANT_PROFILE_BASE);
    const model = Array.isArray(data) ? data[0] : data;
    const apiTimeZone = typeof model?.timeZoneId === "string" && model.timeZoneId.trim()
      ? model.timeZoneId.trim()
      : "";

    setTenantProfile({
      ...EMPTY_TENANT_PROFILE,
      ...(model || {}),
      timeZoneId: apiTimeZone || getBrowserTimeZone(),
    });
    setTimeZoneLoadedFromApi(Boolean(apiTimeZone));
    setTimeZoneTouched(false);
    if (!model?.taxId) setTaxIdVisible(true);
  }, [api]);

  const loadCompanyLogo = useCallback(async () => {
    if (!api || !showLogo) return;

    try {
      const data: any = await api.get(`${TENANT_PROFILE_BASE}/logo`);
      syncLogoState(data);
    } catch (e: any) {
      // 404 means no logo — not an error
      if (e?.status === 404) {
        clearLogoState();
        return;
      }
      console.error(e);
      clearLogoState();
    }
  }, [api, showLogo]);

  useEffect(() => {
    if (!api) return;

    (async () => {
      try {
        await Promise.all([loadStates(), loadTenantProfile(), loadCompanyLogo()]);
      } catch (e: any) {
        console.error(e);
        showStatus("error", e?.message || "Failed to load tenant profile.");
      }
    })();
  }, [api, loadStates, loadTenantProfile, loadCompanyLogo]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setTenantProfile((prev) => ({
      ...(prev || EMPTY_TENANT_PROFILE),
      [name]: value,
    }));
  };

  const saveTenantProfile = async () => {
    if (!api || !tenantProfile) return;

    try {
      const { taxId, ...rest } = tenantProfile;
      const payload: any = {
        ...rest,
        state: String(tenantProfile.state) === "" ? 0 : Number(tenantProfile.state),
      };
      if (!timeZoneLoadedFromApi && !timeZoneTouched) {
        delete payload.timeZoneId;
      }
      // Only include taxId if the user revealed and edited it
      if (revealedTaxId !== null) {
        payload.taxId = revealedTaxId;
      }

      await api.put(TENANT_PROFILE_BASE, payload);

      if (payload.timeZoneId) {
        setTimeZoneLoadedFromApi(true);
        setTimeZoneTouched(false);
      }
      showStatus("success", "Company info updated successfully.");
    } catch (e2: any) {
      console.error(e2);
      showStatus("error", e2?.message || "Failed to update company info.");
    }
  };

  const updateTenantProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await saveTenantProfile();
  };

  const handleChooseLogo = () => {
    if (logoBusy) return;
    fileInputRef.current?.click();
  };

  const handleLogoSelected = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file || !api) return;

    setLogoBusy(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const data: any = await api.postForm(`${TENANT_PROFILE_BASE}/logo`, formData);

      setCompanyLogoUrl(data?.url || "");
      setCompanyLogoFileName(file.name || data?.fileName || "");

      showStatus("success", "Company logo uploaded successfully.");
    } catch (err: any) {
      console.error(err);
      showStatus("error", err?.message || "Failed to upload company logo.");
    } finally {
      setLogoBusy(false);

      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!api || !companyLogoUrl) return;

    setLogoBusy(true);

    try {
      await api.delete(`${TENANT_PROFILE_BASE}/logo`);

      clearLogoState();
      showStatus("success", "Company logo removed successfully.");
    } catch (err: any) {
      console.error(err);
      showStatus("error", err?.message || "Failed to remove company logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const displayLogoFileName = truncateFileName(companyLogoFileName);

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <>
          <div className={styles['company-shell']}>
            {showLogo && (
            <div className={styles['company-card']}>
              <div className={styles['company-card-header']}>
                <h2 className={styles['company-card-header-title']}>Company Logo</h2>
              </div>
              <div className={styles['company-card-body']}>
                <button
                  type="button"
                  className={styles['company-collapse-toggle']}
                  onClick={() => setLogoOpen(o => !o)}
                  aria-expanded={logoOpen}
                >
                  {companyLogoUrl ? (
                    <img src={companyLogoUrl} alt="Company logo" className={styles['company-collapse-thumb']} />
                  ) : (
                    <span className={styles['company-collapse-placeholder']}>No logo</span>
                  )}
                  <span>{logoOpen ? 'Hide Logo Panel' : 'Show Logo Panel'}</span>
                  <span className={`${styles['company-collapse-arrow']} ${logoOpen ? styles['company-collapse-arrow--open'] : ''}`}>▸</span>
                </button>

                {logoOpen && (
                  <div className={styles['company-logo-panel']}>
                    <div className={styles['company-logo-preview']}>
                      {companyLogoUrl ? (
                        <img src={companyLogoUrl} alt="Company logo preview" />
                      ) : (
                        <div className={styles['company-logo-empty']}>
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.5, marginBottom: 6 }}>
                            <rect x="3" y="3" width="18" height="18" rx="3" stroke="#9ca3af" strokeWidth="1.5" />
                            <circle cx="8.5" cy="8.5" r="2" stroke="#9ca3af" strokeWidth="1.5" />
                            <path d="M3 16l5-5 4 4 3-3 6 6" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>No logo uploaded</span>
                        </div>
                      )}
                    </div>

                    <div className={styles['company-logo-meta']}>
                      <div className={styles['company-logo-title']}>Used on reports</div>

                      <div className={styles['company-logo-copy']}>
                        Recommended size: 250 × 144 px. PNG works best for sharp
                        report output. JPG is also supported. Maximum size: 2 MB.
                      </div>

                      {companyLogoUrl && (
                        <div
                          className={styles['company-logo-file']}
                          title={companyLogoFileName || ""}
                        >
                          {displayLogoFileName
                            ? `Logo uploaded • ${displayLogoFileName}`
                            : "Logo uploaded"}
                        </div>
                      )}

                      <div className={styles['company-logo-actions']}>
                        <button
                          type="button"
                          onClick={handleChooseLogo}
                          disabled={logoBusy}
                          className={styles['company-btn']}
                        >
                          {logoBusy
                            ? "Working..."
                            : companyLogoUrl
                              ? "Replace Logo"
                              : "Upload Logo"}
                        </button>

                        {companyLogoUrl && (
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            disabled={logoBusy}
                            className={styles['company-btn-secondary']}
                          >
                            Remove Logo
                          </button>
                        )}
                      </div>

                      <input
                        ref={fileInputRef}
                        id="companyLogoUpload"
                        type="file"
                        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                        onChange={handleLogoSelected}
                        aria-label="Upload company logo"
                        style={{ display: "none" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            <div className={styles['company-card']}>
              <div className={styles['company-card-header']}>
                <h2 className={styles['company-card-header-title']}>Company Details</h2>
              </div>
              <div className={styles['company-card-body']}>
                <form onSubmit={updateTenantProfile} noValidate>
                <div className={styles['company-form-grid']}>
                  <div className={`${styles['company-field']} ${styles['company-field--full']}`}>
                    <label className={styles['company-label']} htmlFor="businessName">
                      Business Name
                    </label>
                    <input
                      id="businessName"
                      className={styles['company-control']}
                      type="text"
                      name="businessName"
                      value={tenantProfile?.businessName || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--half']}`}>
                    <label className={styles['company-label']} htmlFor="firstName">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      className={styles['company-control']}
                      type="text"
                      name="firstName"
                      value={tenantProfile?.firstName || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--half']}`}>
                    <label className={styles['company-label']} htmlFor="lastName">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      className={styles['company-control']}
                      type="text"
                      name="lastName"
                      value={tenantProfile?.lastName || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--full']}`}>
                    <label className={styles['company-label']} htmlFor="streetAddress">
                      Address
                    </label>
                    <input
                      id="streetAddress"
                      className={styles['company-control']}
                      type="text"
                      name="streetAddress"
                      value={tenantProfile?.streetAddress || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--third']}`}>
                    <label className={styles['company-label']} htmlFor="city">
                      City
                    </label>
                    <input
                      id="city"
                      className={styles['company-control']}
                      type="text"
                      name="city"
                      value={tenantProfile?.city || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--third']}`}>
                    <label className={styles['company-label']} htmlFor="state">
                      State
                    </label>
                    <SearchableSelect
                      id="state"
                      className={styles['company-control']}
                      options={states.map((s) => ({ value: s.stateId, label: s.name }))}
                      value={tenantProfile?.state ? Number(tenantProfile.state) : null}
                      onChange={(val) => setTenantProfile((prev: any) => ({ ...prev, state: val }))}
                      placeholder="Search states..."
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--third']}`}>
                    <label className={styles['company-label']} htmlFor="zipCode">
                      Zip
                    </label>
                    <input
                      id="zipCode"
                      className={styles['company-control']}
                      type="text"
                      name="zipCode"
                      value={tenantProfile?.zipCode || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--third']}`}>
                    <label className={styles['company-label']} htmlFor="phoneNumber">
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      className={styles['company-control']}
                      type="text"
                      name="phoneNumber"
                      value={tenantProfile?.phoneNumber || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--third']}`}>
                    <label className={styles['company-label']} htmlFor="taxId">
                      Tax ID
                    </label>
                    <div className={styles['company-control-reveal']}>
                      <input
                        id="taxId"
                        className={styles['company-control']}
                        type="text"
                        name="taxId"
                        readOnly={!taxIdVisible}
                        value={taxIdVisible && revealedTaxId !== null ? revealedTaxId : (tenantProfile?.taxId || "")}
                        onChange={(e) => {
                          if (taxIdVisible) setRevealedTaxId(e.target.value);
                          handleInputChange(e);
                        }}
                      />
                      <button
                        type="button"
                        className={styles['company-reveal-btn']}
                        onClick={toggleTaxIdVisibility}
                        aria-label={taxIdVisible ? "Hide Tax ID" : "Show Tax ID"}
                      >
                        {taxIdVisible ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--third']}`}>
                    <label className={styles['company-label']} htmlFor="emailAddress">
                      Email Address
                    </label>
                    <input
                      id="emailAddress"
                      className={styles['company-control']}
                      type="email"
                      name="emailAddress"
                      value={tenantProfile?.emailAddress || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['company-field']} ${styles['company-field--third']}`}>
                    <label className={styles['company-label']} htmlFor="companyTimeZoneId">
                      Time Zone
                    </label>
                    <input
                      id="companyTimeZoneId"
                      className={styles['company-control']}
                      type="text"
                      name="timeZoneId"
                      value={tenantProfile?.timeZoneId || ""}
                      onChange={(e) => {
                        setTimeZoneTouched(true);
                        handleInputChange(e);
                      }}
                      placeholder={FALLBACK_TIME_ZONE}
                    />
                  </div>

                  <div className={styles['company-form-actions']}>
                    <button
                      type="button"
                      className={`${styles['company-btn']} ${styles['company-save-btn']}`}
                      onClick={saveTenantProfile}
                    >
                      Update
                    </button>
                  </div>
                </div>
                </form>
              </div>
            </div>
          </div>
        {updateStatus && (
          <Toast
            message={updateMessage}
            type={updateStatus as "success" | "error"}
            onClose={() => { setUpdateStatus(null); setUpdateMessage(""); }}
          />
        )}
      </>
    </MsalAuthenticationTemplate>
  );
};

export default Company;